import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	MdArrowBackIosNew,
	MdCheck,
	MdClose,
	MdExpandLess,
	MdExpandMore,
} from 'react-icons/md';
import Cookies from 'js-cookie';
import { Link, useLocation } from 'react-router-dom';

import NavBar from '../NavBar';
import {
	requestAceitarSolicitacaoTroca,
	requestGerenciamentoSolicitacoesTroca,
	requestRecusarSolicitacaoTroca,
} from '../../services/escalaRequests';

const normalizeCode = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim()
		.toUpperCase();

const hasPermission = (permissions = [], permissionName = '') => {
	const target = normalizeCode(permissionName);
	const values = Array.isArray(permissions) ? permissions : [permissions];
	return values.some((permission) => normalizeCode(permission) === target);
};

const getErrorMessage = (response, fallback) => {
	const issueMessages = [
		response?.issues,
		response?.error?.issues,
		response?.error?.data?.issues,
		response?.error?.detail?.issues,
	]
		.filter(Array.isArray)
		.flat()
		.map((issue = {}) => String(issue?.mensagem || '').trim())
		.filter(Boolean);
	if (issueMessages.length > 0) return Array.from(new Set(issueMessages)).join(' ');

	const detail = response?.error?.detail;
	const data = response?.error?.data;
	if (typeof detail === 'string') return detail;
	if (Array.isArray(detail)) return detail.join(' ');
	if (data && typeof data === 'object') {
		const messages = Object.values(data).flat().filter(Boolean);
		if (messages.length) return messages.join(' ');
	}
	return fallback;
};

const formatDate = (value = '') => {
	if (!value) return '--';
	const [datePart] = String(value).split('T');
	const [year, month, day] = datePart.split('-');
	return year && month && day ? `${day}/${month}/${year}` : value;
};

const formatTime = (value = '') =>
	value ? String(value).slice(0, 5) : '--:--';

const formatRelativeTime = (value = '') => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const diffMinutes = Math.max(
		0,
		Math.floor((Date.now() - date.getTime()) / 60000),
	);
	if (diffMinutes < 1) return 'agora';
	if (diffMinutes < 60) return `${diffMinutes} min atrás`;
	const hours = Math.floor(diffMinutes / 60);
	if (hours < 24) return `${hours}h atrás`;
	const days = Math.floor(hours / 24);
	return `${days} ${days === 1 ? 'dia' : 'dias'} atrás`;
};

const statusConfig = {
	PENDENTE: {
		label: 'Não respondida',
		className: 'text-[#0D6E9C]',
	},
	ACEITA: {
		label: 'Solicitação aceita',
		className: 'text-[#13335A]',
		icon: MdCheck,
	},
	RECUSADA: {
		label: 'Solicitação recusada',
		className: 'text-[#C33758]',
		icon: MdClose,
	},
	CANCELADA: {
		label: 'Solicitação cancelada',
		className: 'text-[#8A5B15]',
		icon: MdClose,
	},
	INVALIDADA: {
		label: 'Solicitação invalidada',
		className: 'text-[#6B7280]',
		icon: MdClose,
	},
};

const PlantaoResumo = ({ label, plantao = {} }) => (
	<div className='rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] px-3 py-2'>
		<p className='text-[12px] font-semibold uppercase text-[#7A8793]'>
			{label}
		</p>
		<p className='mt-1 text-[14px] font-medium text-[#13335A]'>
			{formatDate(plantao.data)} - {formatTime(plantao.hora_inicio)}
			{plantao.hora_fim ? ` às ${formatTime(plantao.hora_fim)}` : ''}
		</p>
		<p className='text-[12px] text-[#5F6770]'>
			{plantao.faixa_horaria || 'Turno não informado'}
			{plantao?.setor?.nome || plantao?.setor_nome
				? ` - ${plantao?.setor?.nome || plantao?.setor_nome}`
				: ''}
		</p>
	</div>
);

const TextoTrocaResumido = ({ origem = {}, destino = {} }) => (
	<p className='mt-3 text-left text-[14px] leading-5 text-[#5F6770]'>
		Do dia <span className='font-semibold'>{formatDate(origem.data)}</span>{' '}
		turno{' '}
		<span className='font-semibold'>
			{origem.faixa_horaria || 'não informado'}
		</span>{' '}
		para o dia{' '}
		<span className='font-semibold'>{formatDate(destino.data)}</span> turno{' '}
		<span className='font-semibold'>
			{destino.faixa_horaria || 'não informado'}
		</span>
	</p>
);

const SolicitationCard = ({ item, actionLoading, onAccept, onRefuse }) => {
	const responded = item.status === 'ACEITA' || item.status === 'RECUSADA';
	const [expanded, setExpanded] = useState(!responded);
	const [refusing, setRefusing] = useState(false);
	const [reason, setReason] = useState('');
	const [localError, setLocalError] = useState('');
	const status = statusConfig[item.status] || statusConfig.PENDENTE;
	const StatusIcon = status.icon;
	const canAccept = Boolean(item?.acoes?.pode_aceitar);
	const canRefuse = Boolean(item?.acoes?.pode_recusar);
	const showExpandedContent = !responded || expanded;

	const submitRefusal = async () => {
		if (!reason.trim()) {
			setLocalError('Informe o motivo da recusa.');
			return;
		}
		setLocalError('');
		const succeeded = await onRefuse(item.id, reason.trim());
		if (succeeded) {
			setRefusing(false);
			setReason('');
		}
	};

	return (
		<article
			className={`w-full rounded-[10px] border border-[#E8E8E8] px-4 py-4 shadow-[0_1px_4px_rgba(0,0,0,0.18)] ${
				item.status === 'PENDENTE'
					? 'border-l-[5px] border-l-[#38BDF8] bg-white'
					: 'bg-[#FCFCFC]'
			}`}>
			<div className='flex flex-col gap-3'>
				<div className='flex items-start justify-between gap-4'>
					<div>
						<h2 className='text-left text-[14px] font-semibold text-[#13335A]'>
							{item?.solicitante?.nome ||
								'Profissional solicitante'}
						</h2>
						<p className='text-left text-[14px] text-[#5F6770]'>
							Solicitou troca com{' '}
							<span className='font-semibold'>
								{item?.alvo?.nome || 'profissional de destino'}
							</span>
						</p>
						{responded && !expanded && (
							<TextoTrocaResumido
								origem={item.origem}
								destino={item.destino}
							/>
						)}
					</div>
					<span className='shrink-0 text-[12px] text-[#5F6770]'>
						{formatRelativeTime(item.data_criacao)}
					</span>
				</div>

				{responded && (
					<div className='flex items-center justify-between gap-3'>
						<div
							className={`inline-flex items-center gap-1.5 text-[12px] ${status.className}`}>
							{StatusIcon && <StatusIcon className='h-4 w-4' />}
							<span>{status.label}</span>
						</div>
						<button
							type='button'
							onClick={() => setExpanded((value) => !value)}
							className='inline-flex items-center gap-1 text-[12px] text-[#5F6770]'>
							{expanded ? 'Ver menos' : 'Ver mais'}
							{expanded ? (
								<MdExpandLess className='h-4 w-4' />
							) : (
								<MdExpandMore className='h-4 w-4' />
							)}
						</button>
					</div>
				)}

				{showExpandedContent && (
					<>
						<div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
							<PlantaoResumo
								label='Plantão de origem'
								plantao={item.origem}
							/>
							<PlantaoResumo
								label='Plantão de destino'
								plantao={item.destino}
							/>
						</div>

						<div>
							<p className='mb-1 text-left text-[13px] text-[#4F5257]'>
								Justificativa
							</p>
							<div className='min-h-[48px] rounded-lg border border-[#E4E4E4] bg-[#FBFBFB] px-3 py-2 text-left text-[13px] text-[#6B7280]'>
								{item.justificativa || 'Não informada.'}
							</div>
						</div>

						{(item?.finalizacao?.motivo ||
							item?.finalizacao?.motivo_recusa) && (
							<div>
								<p className='mb-1 text-left text-[13px] text-[#4F5257]'>
									Motivo da finalização
								</p>
								<div className='rounded-lg border border-[#E4E4E4] bg-[#FBFBFB] px-3 py-2 text-left text-[13px] text-[#6B7280]'>
									{item.finalizacao.motivo ||
										item.finalizacao.motivo_recusa}
								</div>
							</div>
						)}

						{(canAccept || canRefuse) && (
							<div className='flex flex-wrap gap-3'>
								{canAccept && (
									<button
										type='button'
										disabled={actionLoading}
										onClick={() => onAccept(item.id)}
										className='
										rounded-md font-inter border border-[#dddddd] bg-white px-8 py-1 text-[13px]  transition text-[#13335A] hover:bg-[#13335A]  hover:text-white  disabled:opacity-50 shadow-md
										'>
										Aceitar
									</button>
								)}
								{canRefuse && (
									<button
										type='button'
										disabled={actionLoading}
										onClick={() =>
											setRefusing((value) => !value)
										}
										className='rounded-md font-inter border border-[#dddddd] bg-white px-8 py-1 text-[13px] text-[#B74644] transition hover:bg-[#B74644] hover:border-[#B74644] hover:text-white disabled:opacity-50 shadow-md'>
										Recusar
									</button>
								)}
							</div>
						)}

						{refusing && canRefuse && (
							<div className='flex flex-col gap-2'>
								<label
									htmlFor={`motivo-recusa-${item.id}`}
									className='text-left text-[13px] text-[#4F5257]'>
									Motivo da recusa
								</label>
								<textarea
									id={`motivo-recusa-${item.id}`}
									value={reason}
									maxLength={1000}
									rows={3}
									placeholder='Informe a justificativa'
									onChange={(event) =>
										setReason(event.target.value)
									}
									className='w-full rounded-lg border border-[#D1D5DB] px-3 py-2 text-[13px] outline-none text-[#858585] focus:border-[#13335A] '
								/>
								{localError && (
									<p className='text-left text-[12px] text-[#B74644]'>
										{localError}
									</p>
								)}
								<button
									type='button'
									disabled={actionLoading}
									onClick={submitRefusal}
									className='w-fit bg-white border border-[#D1D5DB] hover:border-[#B74644] text-[#7c7c7c] hover:bg-[#B74644] px-7 py-1  rounded-md text-[13px] hover:text-white disabled:opacity-50 duration-300 transiton-all'>
									Enviar
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</article>
	);
};

function Solicitacoes() {
	const location = useLocation();
	const [filter, setFilter] = useState('todas');
	const [requests, setRequests] = useState([]);
	const [summary, setSummary] = useState({ total: 0, nao_respondidas: 0 });
	const [loading, setLoading] = useState(true);
	const [actionLoadingId, setActionLoadingId] = useState(null);
	const [acceptConfirmationId, setAcceptConfirmationId] = useState(null);
	const [feedback, setFeedback] = useState({ type: '', message: '' });
	const [page, setPage] = useState(1);
	const [pagination, setPagination] = useState({
		next: null,
		previous: null,
	});

	const user = useMemo(() => {
		try {
			return JSON.parse(Cookies.get('user') || '{}');
		} catch (_error) {
			return {};
		}
	}, []);
	const permissions = Array.isArray(user?.permissao)
		? user.permissao
		: [user?.permissao].filter(Boolean);
	const isDeveloper = normalizeCode(user?.cargo) === 'DESENVOLVEDOR';
	const modalidade =
		normalizeCode(location.state?.modalidade) === 'ENFERMAGEM'
			? 'ENFERMAGEM'
			: 'MEDICA';
	const requiredPermission =
		modalidade === 'ENFERMAGEM'
			? 'EscalaEnfermagemEdicao'
			: 'EscalaMedicaEdicao';
	const hasAccess =
		isDeveloper || hasPermission(permissions, requiredPermission);
	const userCnes = String(
		user?.cnes ||
			user?.cnes_estabelecimento ||
			user?.cnesEstabelecimento ||
			'',
	).trim();
	const selectedEstablishment = String(
		location.state?.estabelecimento || '',
	).trim();
	const establishment = isDeveloper
		? selectedEstablishment
		: userCnes || selectedEstablishment;
	const escalaOrigem = modalidade === 'ENFERMAGEM' ? 'enfermagem' : 'medica';

	const loadRequests = useCallback(async () => {
		if (!user?.token || !hasAccess || !establishment) {
			setLoading(false);
			return;
		}

		setLoading(true);
		const response = await requestGerenciamentoSolicitacoesTroca({
			token: user.token,
			modalidade,
			estabelecimento: establishment,
			status: filter === 'naoRespondidas' ? 'PENDENTE' : undefined,
			page,
		});

		if (response?.error) {
			setRequests([]);
			setFeedback({
				type: 'error',
				message: getErrorMessage(
					response,
					'Não foi possível carregar as solicitações.',
				),
			});
			setLoading(false);
			return;
		}

		setRequests(
			Array.isArray(response)
				? response
				: Array.isArray(response?.results)
					? response.results
					: [],
		);
		setSummary(response?.resumo || { total: response?.count || 0 });
		setPagination({
			next: response?.next || null,
			previous: response?.previous || null,
		});
		setLoading(false);
	}, [establishment, filter, hasAccess, modalidade, page, user?.token]);

	useEffect(() => {
		loadRequests();
	}, [loadRequests]);

	const handleAccept = (id) => {
		setAcceptConfirmationId(id);
	};

	const confirmAccept = async () => {
		const id = acceptConfirmationId;
		if (!id) return false;

		setActionLoadingId(id);
		const response = await requestAceitarSolicitacaoTroca({
			token: user.token,
			id,
		});
		setActionLoadingId(null);
		if (response?.error) {
			setAcceptConfirmationId(null);
			setFeedback({
				type: 'error',
				message: getErrorMessage(
					response,
					'Não foi possível aceitar a solicitação.',
				),
			});
			if (response.error.status === 409) await loadRequests();
			return false;
		}
		setAcceptConfirmationId(null);
		setFeedback({ type: '', message: '' });
		await loadRequests();
		return true;
	};

	const handleRefuse = async (id, reason) => {
		setActionLoadingId(id);
		const response = await requestRecusarSolicitacaoTroca({
			token: user.token,
			id,
			motivoRecusa: reason,
		});
		setActionLoadingId(null);
		if (response?.error) {
			setFeedback({
				type: 'error',
				message: getErrorMessage(
					response,
					'Não foi possível recusar a solicitação.',
				),
			});
			return false;
		}
		setFeedback({ type: '', message: '' });
		await loadRequests();
		return true;
	};

	const filterButtonClass = (active) =>
		`rounded-[10px] px-4 py-2 text-[13px] leading-5 transition ${
			active
				? 'bg-[#13335A] text-white'
				: 'bg-[#F3F4F6] text-[#5F6770] hover:bg-[#E8EBEF]'
		}`;

	if (!hasAccess || !establishment) {
		return (
			<div className='flex min-h-screen flex-col bg-white font-inter'>
				<NavBar />
				<main className='mt-[80px] flex flex-1 items-center justify-center px-4'>
					<div className='w-full max-w-[680px] rounded-lg bg-[#13335A] px-6 py-12 text-center text-white'>
						<h1 className='text-3xl font-semibold'>
							Acesso indisponível
						</h1>
						<p className='mt-4 text-[#D8E1EA]'>
							{!hasAccess
								? 'Esta página exige permissão de edição para a modalidade selecionada.'
								: 'Selecione uma unidade no gerenciamento de profissionais antes de acessar as solicitações.'}
						</p>
						<Link
							to='/escala-profissionais'
							state={{ escalaOrigem }}
							className='mt-7 inline-flex rounded-md bg-white px-5 py-2 text-[#13335A]'>
							Voltar
						</Link>
					</div>
				</main>
			</div>
		);
	}

	return (
		<div className='flex min-h-screen flex-col bg-white font-inter'>
			<NavBar />
			<section className='mt-[80px] w-full bg-[#E9EDF2]'>
				<div className='mx-auto flex w-[95%] max-w-[1200px] justify-center px-4 py-9'>
					<h1 className='text-center text-[26px] font-semibold leading-10 text-[#13335A] sm:text-[28px]'>
						Gerenciamento de solicitações
					</h1>
				</div>
			</section>

			<main className='mx-auto flex w-[95%] max-w-[880px] flex-1 flex-col gap-5 px-2 py-8 sm:px-0'>
				<Link
					to='/escala-profissionais'
					state={{ escalaOrigem }}
					className='inline-flex w-fit items-center gap-1.5 text-[18px] font-medium text-[#17395f] transition-colors hover:text-[#60758f]'>
					<MdArrowBackIosNew size={15} />
					Voltar
				</Link>

				<header className='flex flex-col gap-4 rounded-[10px] border border-[#E5E7EB] bg-white px-6 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.18)] sm:flex-row sm:items-center sm:justify-between'>
					<div className='text-left'>
						<h2 className='text-[22px] font-normal leading-8 text-[#4F5257]'>
							Solicitações
						</h2>
						<p className='text-[13px] text-[#9CA3AF]'>
							{modalidade === 'MEDICA'
								? 'Escala médica'
								: 'Escala de enfermagem'}{' '}
							- unidade {establishment}
						</p>
					</div>

					<div className='flex flex-wrap gap-3'>
						<button
							type='button'
							onClick={() => {
								setFilter('todas');
								setPage(1);
							}}
							className={filterButtonClass(filter === 'todas')}>
							Todas
						</button>
						<button
							type='button'
							onClick={() => {
								setFilter('naoRespondidas');
								setPage(1);
							}}
							className={filterButtonClass(
								filter === 'naoRespondidas',
							)}>
							Não respondidas{' '}
							<span className='ml-1'>
								{summary.nao_respondidas || 0}
							</span>
						</button>
					</div>
				</header>

				{feedback.type === 'error' && feedback.message && (
					<div className='rounded-md border border-[#F0C7C7] bg-[#FFF4F4] px-4 py-3 text-center text-[13px] text-[#A04141]'>
						{feedback.message}
					</div>
				)}

				{loading ? (
					<div className='py-16 text-center text-[#6B7280]'>
						Carregando solicitações...
					</div>
				) : requests.length ? (
					<div className='flex flex-col gap-3'>
						{requests.map((item) => (
							<SolicitationCard
								key={item.id}
								item={item}
								actionLoading={actionLoadingId === item.id}
								onAccept={handleAccept}
								onRefuse={handleRefuse}
							/>
						))}
					</div>
				) : (
					<div className='rounded-lg border border-[#E5E7EB] py-14 text-center text-[#6B7280]'>
						Nenhuma solicitação encontrada.
					</div>
				)}

				{(pagination.previous || pagination.next) && (
					<div className='flex items-center justify-center gap-3'>
						<button
							type='button'
							disabled={!pagination.previous || loading}
							onClick={() =>
								setPage((value) => Math.max(1, value - 1))
							}
							className='rounded border border-[#D1D5DB] px-4 py-2 text-[13px] text-[#13335A] disabled:opacity-40'>
							Anterior
						</button>
						<span className='text-[13px] text-[#6B7280]'>
							Página {page}
						</span>
						<button
							type='button'
							disabled={!pagination.next || loading}
							onClick={() => setPage((value) => value + 1)}
							className='rounded border border-[#D1D5DB] px-4 py-2 text-[13px] text-[#13335A] disabled:opacity-40'>
							Próxima
						</button>
					</div>
				)}
			</main>

			{acceptConfirmationId !== null && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4'>
					<div
						role='dialog'
						aria-modal='true'
						aria-labelledby='confirmar-aceite-troca'
						className='w-full max-w-[420px] rounded-lg border border-[#E5E7EB] bg-white p-6 shadow-2xl'>
						<h2
							id='confirmar-aceite-troca'
							className='text-center text-[18px] font-semibold text-[#13335A]'>
							Deseja realizar a troca dos plantões?
						</h2>

						<div className='mt-6 flex justify-center gap-3'>
							<button
								type='button'
								disabled={
									actionLoadingId === acceptConfirmationId
								}
								onClick={() => setAcceptConfirmationId(null)}
								className='min-w-[110px] rounded-md border border-[#D1D5DB] bg-white px-5 py-2 text-[13px] text-[#5F6770] transition hover:bg-[#F3F4F6] disabled:cursor-not-allowed disabled:opacity-50'>
								Cancelar
							</button>
							<button
								type='button'
								disabled={
									actionLoadingId === acceptConfirmationId
								}
								onClick={confirmAccept}
								className='min-w-[110px] rounded-md bg-[#13335A] px-5 py-2 text-[13px] text-white transition hover:bg-[#0D2949] disabled:cursor-not-allowed disabled:opacity-50'>
								{actionLoadingId === acceptConfirmationId
									? 'Enviando...'
									: 'Enviar'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default Solicitacoes;
